import type { Category } from '@/types/domain';
import { cn } from '@/lib/utils';

/**
 * FlavorTile — small visual representation of an item. Renders an accent band
 * (per-flavor color, applied inline only) over a neutral Brix-tinted cardboard
 * background, with a tiny category label so the tile self-identifies.
 *
 * Per-flavor accent colors are the ONE place we apply non-token color inline,
 * per the Brix design rules.
 */
interface FlavorTileProps {
  accent: string;
  category: Category;
  size?: 'sm' | 'md';
  className?: string;
}

const SIZE_PX: Record<NonNullable<FlavorTileProps['size']>, number> = {
  sm: 40,
  md: 56,
};

function categoryLabel(category: Category): string {
  switch (category) {
    case '3-gal BIB':
    case '5-gal BIB':
      return 'BIB';
    case 'Sugar Free':
      return 'SF';
    case '24-Pack':
      return '24';
    case 'Gas':
      return 'CO₂';
    case 'Mixer':
      return 'MIX';
    case 'Other':
      return '•';
  }
}

export function FlavorTile({ accent, category, size = 'md', className }: FlavorTileProps) {
  const px = SIZE_PX[size];
  const labelText = size === 'sm' ? '' : categoryLabel(category);

  return (
    <div
      className={cn(
        'relative shrink-0 rounded-lg overflow-hidden border border-brix-border bg-brix-surface-alt',
        className,
      )}
      style={{ width: px, height: px }}
      aria-hidden="true"
    >
      {/* Accent band — the flavor color band on the "label" of the BIB box. */}
      <div
        className="absolute left-0 right-0"
        style={{ background: accent, top: '38%', height: '32%' }}
      />
      {/* Category micro-label */}
      {labelText && (
        <span
          className="absolute top-0.5 left-0 right-0 text-center text-[7px] font-bold tracking-tight text-brix-muted/70"
        >
          {labelText}
        </span>
      )}
      {/* Bottom marker — represents a corner detail / cap. */}
      <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-sm bg-brix-muted/40" />
    </div>
  );
}
